import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';

const ClientInteractionsTable = ({ interactions, onClientSelect, selectedClients, onBulkAction }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'lastContact', direction: 'desc' });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success/10 text-success';
      case 'pending':
        return 'bg-warning/10 text-warning';
      case 'inactive':
        return 'bg-muted text-muted-foreground';
      case 'urgent':
        return 'bg-error/10 text-error';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high':
        return <Icon name="ArrowUp" size={14} className="text-error" />;
      case 'medium':
        return <Icon name="Minus" size={14} className="text-warning" />;
      case 'low':
        return <Icon name="ArrowDown" size={14} className="text-success" />;
      default:
        return null;
    }
  };

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      onClientSelect(interactions?.map(i => i?.clientId));
    } else {
      onClientSelect([]);
    }
  };

  const handleSelectOne = (clientId, checked) => {
    if (checked) {
      onClientSelect([...selectedClients, clientId]);
    } else {
      onClientSelect(selectedClients?.filter(id => id !== clientId));
    }
  };

  return (
    <div className="h-full flex flex-col bg-card">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Icon name="Users" size={20} />
            Letzte Kundeninteraktionen
          </h2>
          <div className="flex items-center gap-2">
            <button className="p-2 hover:bg-muted rounded-md transition-colors">
              <Icon name="Filter" size={18} />
            </button>
            <button className="p-2 hover:bg-muted rounded-md transition-colors">
              <Icon name="Download" size={18} />
            </button>
          </div>
        </div>

        {selectedClients?.length > 0 && (
          <div className="flex items-center gap-2 p-2 bg-primary/10 rounded-md">
            <span className="text-sm font-medium text-primary">
              {selectedClients?.length} ausgewählt
            </span>
            <div className="flex gap-2 ml-auto">
              <button
                onClick={() => onBulkAction('status')}
                className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded hover:opacity-90"
              >
                Status ändern
              </button>
              <button
                onClick={() => onBulkAction('appointment')}
                className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded hover:opacity-90"
              >
                Termin planen
              </button>
              <button
                onClick={() => onBulkAction('export')}
                className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded hover:opacity-90"
              >
                Exportieren
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-auto scrollbar-thin">
        <table className="w-full">
          <thead className="bg-muted sticky top-0 z-10">
            <tr>
              <th className="p-3 text-left">
                <Checkbox
                  checked={selectedClients?.length === interactions?.length}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                />
              </th>
              <th
                className="p-3 text-left text-sm font-semibold text-foreground cursor-pointer hover:bg-muted-foreground/10"
                onClick={() => handleSort('clientId')}
              >
                <div className="flex items-center gap-2">
                  Kunden-ID
                  <Icon name="ArrowUpDown" size={14} />
                </div>
              </th>
              <th
                className="p-3 text-left text-sm font-semibold text-foreground cursor-pointer hover:bg-muted-foreground/10"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center gap-2">
                  Name
                  <Icon name="ArrowUpDown" size={14} />
                </div>
              </th>
              <th
                className="p-3 text-left text-sm font-semibold text-foreground cursor-pointer hover:bg-muted-foreground/10"
                onClick={() => handleSort('lastContact')}
              >
                <div className="flex items-center gap-2">
                  Letzter Kontakt
                  <Icon name="ArrowUpDown" size={14} />
                </div>
              </th>
              <th className="p-3 text-left text-sm font-semibold text-foreground">Status</th>
              <th className="p-3 text-left text-sm font-semibold text-foreground">Nächste Aktion</th>
              <th className="p-3 text-left text-sm font-semibold text-foreground">Priorität</th>
              <th className="p-3 text-left text-sm font-semibold text-foreground">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {interactions?.map((interaction) => (
              <tr
                key={interaction?.clientId}
                className="border-b border-border hover:bg-muted/50 transition-colors"
              >
                <td className="p-3">
                  <Checkbox
                    checked={selectedClients?.includes(interaction?.clientId)}
                    onChange={(e) => handleSelectOne(interaction?.clientId, e?.target?.checked)}
                  />
                </td>
                <td className="p-3">
                  <span className="font-mono text-sm text-primary font-medium">
                    {interaction?.clientId}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium text-sm">
                      {interaction?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                    </div>
                    <div>
                      <div className="font-medium text-sm text-foreground">{interaction?.name}</div>
                      <div className="text-xs text-muted-foreground">{interaction?.occupation}</div>
                    </div>
                  </div>
                </td>
                <td className="p-3">
                  <div className="text-sm text-foreground">{interaction?.lastContact}</div>
                  <div className="text-xs text-muted-foreground">{interaction?.contactType}</div>
                </td>
                <td className="p-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(interaction?.status)}`}>
                    {interaction?.statusLabel}
                  </span>
                </td>
                <td className="p-3">
                  <div className="text-sm text-foreground">{interaction?.nextAction}</div>
                  <div className="text-xs text-muted-foreground">{interaction?.nextActionDate}</div>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-1">
                    {getPriorityIcon(interaction?.priority)}
                    <span className="text-sm capitalize">{interaction?.priority}</span>
                  </div>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => onClientSelect([interaction?.clientId])}
                      className="p-1.5 hover:bg-muted rounded transition-colors"
                      title="Profil anzeigen"
                    >
                      <Icon name="Eye" size={16} />
                    </button>
                    <button
                      className="p-1.5 hover:bg-muted rounded transition-colors"
                      title="Bearbeiten"
                    >
                      <Icon name="Edit" size={16} />
                    </button>
                    <button
                      className="p-1.5 hover:bg-muted rounded transition-colors"
                      title="Nachricht senden"
                    >
                      <Icon name="Mail" size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="p-4 border-t border-border flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Zeige {interactions?.length} von {interactions?.length} Einträgen
        </div>
        <div className="flex items-center gap-2">
          <button className="px-3 py-1.5 border border-border rounded hover:bg-muted transition-colors text-sm">
            Zurück
          </button>
          <button className="px-3 py-1.5 bg-primary text-primary-foreground rounded hover:opacity-90 transition-opacity text-sm">
            Weiter
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientInteractionsTable;